import { Alert, InputAdornment } from '@mui/material';
import { DateTime } from 'luxon';
import {
  BenefitActivationDTO,
  BenefitDTOBenefitEnum,
  BenefitTemplateItemDTO,
  CouponsBenefitOptionsDTO,
  CreateBenefitActivationDTO,
  CustomBenefitDTO,
  TenantDTO,
  UpdateBenefitActivationDTO,
} from 'probonio-shared-ui/api';
import { useTenant } from 'probonio-shared-ui/module/me';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { TextFieldControl } from '../../../../component/form';
import { BenefitActivationDialogExtensionInterface } from '../../../../extension/interface/benefit-activation-dialog.extension.interface';
import { ActivationDialogExtensions } from '../../ActivationDialog';
import { NonCashLimitWarning } from '../../ActivationDialog/NonCashLimitWarning';
import { NonCashBenefit, NonCashBenefitOptions } from '../../nonCashBenefit/const';
import { NonCashAvailableCouponsAccordion } from '../../nonCashBenefit/NonCashAvailableCouponsAccordion';
import { SelectNonCashBenefitsForm } from '../../nonCashBenefit/SelectNonCashBenefitsForm';

const Header: React.FC = () => {
  const { t } = useTranslation('benefitsModule');
  const { tenant } = useTenant();
  const { watch } = useFormContext();
  const { employeeId } = useParams();
  const benefitOptions: CouponsBenefitOptionsDTO = watch('benefitOptions');
  const editMode = watch('editMode');

  const isAfterDeadline = tenant?.couponOrderDeadline && DateTime.now().day > tenant.couponOrderDeadline;

  const willExceedBudget = DateTime.now().day > 25 && (tenant?.couponBudget || 0) < benefitOptions.monthlyBudget;

  return (
    <>
      {isAfterDeadline && !editMode ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('COUPONS.deadlineActivationWarning')}
        </Alert>
      ) : null}
      <NonCashLimitWarning
        employeeId={employeeId}
        benefit={BenefitDTOBenefitEnum.Coupons}
        monthlyBudget={benefitOptions.monthlyBudget * 100}
      />
      {willExceedBudget && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {t('budgetExceededWarning')}
        </Alert>
      )}
    </>
  );
};

const Fields: React.FC<{ enforcedNonCashBenefits?: readonly NonCashBenefit[] }> = ({ enforcedNonCashBenefits }) => {
  const { watch } = useFormContext();
  const benefitOptions: CouponsBenefitOptionsDTO = watch('benefitOptions');

  return (
    <>
      <SelectNonCashBenefitsForm enforcedNonCashBenefits={enforcedNonCashBenefits} />
      <NonCashAvailableCouponsAccordion
        nonCashBenefits={benefitOptions.nonCashBenefits}
        couponBudget={benefitOptions.monthlyBudget * 100}
      />
    </>
  );
};

interface InlineFieldsProps {
  disabled?: boolean;
}

const InlineField: React.FC<InlineFieldsProps> = ({ disabled }) => {
  const { t } = useTranslation('benefitsModule');
  const { control } = useFormContext();
  return (
    <TextFieldControl
      slotProps={{
        input: {
          endAdornment: <InputAdornment position="end">€</InputAdornment>,
        },
        htmlInput: { step: '0.01' },
      }}
      rules={{
        required: true,
        min: 5,
        max: 50,
      }}
      type="number"
      control={control}
      variant="outlined"
      name="benefitOptions.monthlyBudget"
      label={t('COUPONS.fields.monthlyBudget')}
      data-test-id="activate-dialog-monthly-budget"
      margin="dense"
      fullWidth
      disabled={disabled}
    />
  );
};

/** Reused by Wellpass membership import (FormProvider with `benefitOptions` only; no start/end dates). */
export { Fields, InlineField };

const resetCouponsBenefitActivationOptions: ActivationDialogExtensions<CouponsBenefitOptionsDTO>['onResetBenefitOptions'] = (
  selectedActivation?: BenefitActivationDTO | BenefitTemplateItemDTO,
  context?: { tenant?: TenantDTO; customBenefit?: CustomBenefitDTO },
): NonCashBenefitOptions => {
  return {
    monthlyBudget: (selectedActivation?.couponsOptions ? selectedActivation.couponsOptions.monthlyBudget : 5000) / 100,
    nonCashBenefits: selectedActivation?.couponsOptions?.nonCashBenefits.length
      ? selectedActivation.couponsOptions.nonCashBenefits
      : (context?.tenant?.nonCashBenefits ?? []),
  };
};

const mapCouponsOptions = (benefitOptions: NonCashBenefitOptions): NonCashBenefitOptions => ({
  monthlyBudget: Math.round(benefitOptions.monthlyBudget * 100),
  nonCashBenefits: benefitOptions.nonCashBenefits,
});

const createCouponsBenefitActivationOptions: ActivationDialogExtensions<NonCashBenefitOptions>['onCreateActivation'] = async (
  baseValues,
  benefitOptions,
  saveActivation,
) => {
  const create: CreateBenefitActivationDTO = {
    ...baseValues,
    benefit: BenefitDTOBenefitEnum.Coupons,
    couponsOptions: mapCouponsOptions(benefitOptions) as CouponsBenefitOptionsDTO,
  };
  await saveActivation(create);
};

const updateCouponsBenefitActivation: ActivationDialogExtensions<NonCashBenefitOptions>['onUpdateActivation'] = async (
  baseValues,
  benefitOptions,
  saveActivation,
) => {
  const update: UpdateBenefitActivationDTO = {
    ...baseValues,
    couponsOptions: mapCouponsOptions(benefitOptions),
  };
  await saveActivation(update);
};

export class CouponsBenefitActivationDialogExtension extends BenefitActivationDialogExtensionInterface<CouponsBenefitOptionsDTO> {
  CreateFields?: React.ReactNode = (<Fields />);
  ChangeOptionsFields?: React.ReactNode = (<Fields />);
  Header?: React.ComponentType = Header;
  InlineField?: React.ComponentType = InlineField;
  earliestStartDate?: () => DateTime = () => DateTime.now().plus({ months: DateTime.now().day > 25 ? 1 : 0 });

  onCreateActivation = createCouponsBenefitActivationOptions;
  onUpdateActivation = updateCouponsBenefitActivation;
  onResetBenefitOptions? = resetCouponsBenefitActivationOptions;
}
